import { startsWith, endsWith, isPlainObject, isString, isEmpty } from 'lodash'
import { toAbsolutePath, metadataByPath } from '../form'
import renderTemplate from './render_template'

function isStringValue(value) {
  if (startsWith(value, "'") && endsWith(value, "'")) return true

  if (startsWith(value, '"') && endsWith(value, '"')) return true

  return false
}

export function ensureValueIsConstant(value) {
  if (!isString(value)) return value

  return isStringValue(value) ? value : `'${value}'`
}

export function analyzeValue(value, request) {
  if (isPlainObject(value))
    // TODO: Check for proper syntax of template object
    return renderTemplate(value, request)

  if (typeof value != 'string') return value

  if (isStringValue(value)) return value.slice(1, -1)

  const path = toAbsolutePath(value, request.relativeTo)
  return request.pathMap[path]
}

export const translateValue = analyzeValue

export class Comparisons {
  static #compare(a, b) {
    if (a instanceof Date || b instanceof Date) {
      return this.#compareDates(a, b)
    }

    if (typeof a === 'string' && typeof b === 'string') {
      return this.#compareStrings(a, b)
    }

    return this.#defaultCompare(a, b)
  }

  static #compareDates(a, b) {
    const [_a, _b] = [a, b].map((val) => {
      if (val instanceof Date) {
        return val.getTime()
      } else {
        return new Date(val).getTime()
      }
    })

    return this.#defaultCompare(_a, _b)
  }

  static #compareStrings(a, b) {
    return a.localeCompare(b)
  }

  static #defaultCompare(a, b) {
    if (a < b) return -1
    if (a === b) return 0
    if (a > b) return 1
  }

  static gt(a, b) {
    return this.#compare(a, b) === 1
  }

  static eq(a, b) {
    return this.#compare(a, b) === 0
  }

  static lt(a, b) {
    return this.#compare(a, b) === -1
  }

  static gte(a, b) {
    return [0, 1].includes(this.#compare(a, b))
  }

  static lte(a, b) {
    return [-1, 0].includes(this.#compare(a, b))
  }
}

const CONDITIONS = [
  '_and',
  '_or',
  '_not',

  '_eq',
  '_gt',
  '_gte',
  '_lt',
  '_lte',
  '_between',
  '_in',

  'eq',
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
  'in',
]

export const getPathsFromConditions = (condition) => {
  const collectPaths = (node) => {
    if (isEmpty(node)) return []
    if (typeof node === 'string') {
      return isStringValue(node) ? [] : [node]
    }
    if (Array.isArray(node)) return node.flatMap(collectPaths)

    if (isPlainObject(node) && node.func) {
      if (node.values) return collectPaths(node.values)
      if (node.value) return collectPaths(node.value)
      return []
    }

    return Object.entries(node).flatMap(([key, value]) => {
      if (CONDITIONS.includes(key)) return collectPaths(value)
      let results = [key]
      if (isPlainObject(value)) {
        results = results.concat(collectPaths(value))
      } else if (!isStringValue(value)) {
        results.push(value)
      }
      return results
    })
  }

  return [...new Set(collectPaths(condition))];
};

export const getPathsFromTemplate = (input) => {
  const collectPaths = (node) => {
    if (typeof node === 'string') {
      return isEmpty(node) || isStringValue(node) ? [] : [node]
    }
    if (Array.isArray(node)) return node.flatMap(collectPaths)

    if (isPlainObject(node)) {
      const paths = []

      if (isPlainObject(node.variables))
        paths.push(...Object.values(node.variables).flatMap(collectPaths))

      if (node.if) paths.push(...getPathsFromConditions(node.if))
      if (node.then) paths.push(...collectPaths(node.then))
      if (node.else) paths.push(...collectPaths(node.else))
      if (node.values) paths.push(...collectPaths(node.values))
      if (node.value) paths.push(...collectPaths(node.value))
      if (isString(node.path) && !isStringValue(node.path)) paths.push(node.path)

      return paths
    }
    return []
  }

  return [...new Set(collectPaths(input))]
}
